import Layout from '@/components/ux/Layout';
import PageSearchFormInput from '@/components/ux/PageSearchFormInput';
import { Flex, Table, TBody, Td, Th, THead, Tr, Typography, Tag, TablePagination } from '@alpine-iq/ui-library';
import React from 'react';
import { useNeedsAttentionStore } from '@/stores/campaigns/useNeedsAttentionStore';
import { formatTimestamp, isZeroDate, utils } from '@/helpers';
import { useUsernamesStore } from '@/stores';
import Avatar from '@/components/avatar/Avatar';
import { getCoMarketingBudgetForUser } from '../coMarketing.utils';
import { onSelectCampaign } from '@/pages/campaigns/CampaignsPage';
import { CoMarketingStatusColumnMapping } from '../components/CoMarketingTableHeaders';
import { CoMarketingEditRequestWithTemplateAndCampaign, InstalledCoMarketingTemplateWithCampaign, SponsoredCoMarketingTemplateWithCampaign } from '@/types';
import { useSearchDebounce } from '@/hooks/useSearchDebounce';

export default function NeedsAttentionPage() {
	const [inputValue, search, setSearch] = useSearchDebounce();
	const [pagination, setPagination] = React.useState({
		page: 1,
		limit: 10,
	});
	const { loaded: needsAttentionLoaded, combinedItems, initialize } = useNeedsAttentionStore();
	const { usernames, getUsername } = useUsernamesStore();

	React.useEffect(() => {
		if (!needsAttentionLoaded) {
			initialize();
		}
	}, [needsAttentionLoaded, initialize]);

	const filteredCombinedItems = React.useMemo(() => {
		const searchCleaned = search.trim().toLowerCase();
		if (!searchCleaned) {
			return combinedItems;
		}
		return combinedItems.filter((item) => {
			if (!('type' in item)) {
				const editRequest = item as CoMarketingEditRequestWithTemplateAndCampaign;
				return editRequest.template?.campaign?.name?.toLowerCase().includes(searchCleaned);
			}
			if (item.type === 'sponsored') {
				const sponsoredTemplate = item as SponsoredCoMarketingTemplateWithCampaign;
				return sponsoredTemplate.campaign?.name?.toLowerCase().includes(searchCleaned);
			}
			if (item.type === 'installed') {
				const installedTemplate = item as InstalledCoMarketingTemplateWithCampaign;
				return installedTemplate.campaign?.name?.toLowerCase().includes(searchCleaned);
			}
			return false;
		});
	}, [combinedItems, search]);

	const slicedCombinedItems = React.useMemo(() => {
		return filteredCombinedItems.slice((pagination.page - 1) * pagination.limit, pagination.page * pagination.limit);
	}, [filteredCombinedItems, pagination]);

	const renderedItems = React.useMemo(() => {
		return slicedCombinedItems.map((item) => {
			// Edit request item
			if (!('type' in item)) {
				const editRequest = item as CoMarketingEditRequestWithTemplateAndCampaign;
				const installedTemplate = editRequest.template;
				const userID = editRequest.triggerUID;
				const username = usernames[userID] ?? getUsername(userID, true);
				const approvalStatus = editRequest.approvalStatus;
				const colorData = CoMarketingStatusColumnMapping?.[approvalStatus];
				const campaign = editRequest?.template?.campaign;

				const borderColor = colorData?.style?.borderColor ?? 'var(--gray200)';
				const backgroundColor = colorData?.style?.backgroundColor ?? 'var(--gray100)';
				const textColor = colorData?.style?.color ?? 'var(--text-primary)';

				return (
					<Tr
						key={item.id}
						style={{ cursor: 'pointer' }}
						onClick={() => {
							const isInstaller = installedTemplate?.userID === utils.uid;
							if (campaign?.id) {
								onSelectCampaign(campaign?.id, isInstaller ? undefined : installedTemplate?.id?.toString());
							}
						}}
					>
						<Td>
							<Tag
								style={{
									borderColor: borderColor,
									backgroundColor: backgroundColor,
									color: textColor,
								}}
							>
								<span style={{ color: textColor }}>{approvalStatus === 'pending' ? 'New Edits' : 'Edits Denied'}</span>
							</Tag>
						</Td>
						<Td>
							<Flex
								align="center"
								gap="8"
							>
								<Avatar
									uid={userID}
									size={24}
								/>
								<Typography variant="body">{username}</Typography>
							</Flex>
						</Td>
						<Td>
							<Typography
								variant="body"
								style={{ fontWeight: 700 }}
							>
								{campaign?.name}
							</Typography>
						</Td>
						<Td>-</Td>
						<Td>{installedTemplate?.isPaid && getCoMarketingBudgetForUser(installedTemplate, installedTemplate?.userID)}</Td>
					</Tr>
				);
			}

			// Sponsored template item
			if (item.type === 'sponsored') {
				const template = item as SponsoredCoMarketingTemplateWithCampaign;
				const userID = template.userID;
				const username = usernames[userID] ?? getUsername(userID, true);

				return (
					<Tr
						key={item.id}
						style={{ cursor: 'pointer' }}
						onClick={() => {
							onSelectCampaign(template.campaign?.id, template.id?.toString());
						}}
					>
						<Td>
							<Tag
								style={{
									borderColor: 'var(--green100)',
									backgroundColor: 'var(--green100)',
									color: 'var(--green500)',
								}}
							>
								Proposal
							</Tag>
						</Td>
						<Td>
							<Flex
								align="center"
								gap="8"
							>
								<Avatar
									uid={userID}
									size={24}
								/>
								<Typography variant="body">{username}</Typography>
							</Flex>
						</Td>
						<Td>
							<Typography
								variant="body"
								style={{ fontWeight: 700 }}
							>
								{template.campaign?.name}
							</Typography>
						</Td>
						<Td>
							{!isZeroDate(template.startDate) && formatTimestamp(template.startDate, { format: 'MM/DD/YYYY' })}
							{!isZeroDate(template.startDate) && !isZeroDate(template.endDate) && ' - '}
							{!isZeroDate(template.endDate) && formatTimestamp(template.endDate, { format: 'MM/DD/YYYY' })}
						</Td>
						<Td>{template.isPaid && getCoMarketingBudgetForUser(template, utils.uid)}</Td>
					</Tr>
				);
			}

			// Installed template item
			const template = ((item as unknown as CoMarketingEditRequestWithTemplateAndCampaign)?.template || item) as InstalledCoMarketingTemplateWithCampaign;
			const userID = template.installedFromUserID ?? '';
			const username = usernames[userID] ?? getUsername(userID, true);

			return (
				<Tr
					key={item.id}
					style={{ cursor: 'pointer' }}
					onClick={() => {
						onSelectCampaign(template.campaignID);
					}}
				>
					<Td>
						<Tag
							style={{
								borderColor: 'var(--green100)',
								backgroundColor: 'var(--green100)',
								color: 'var(--green500)',
							}}
						>
							Ready to Launch
						</Tag>
					</Td>
					<Td>
						<Flex
							align="center"
							gap="8"
						>
							<Avatar
								uid={userID}
								size={24}
							/>
							<Typography variant="body">{username}</Typography>
						</Flex>
					</Td>
					<Td>
						<Typography
							variant="body"
							style={{ fontWeight: 700 }}
						>
							{template.campaign?.name}
						</Typography>
					</Td>
					<Td>{!isZeroDate(template.created) && formatTimestamp(template.created, { format: 'MM/DD/YYYY' })}</Td>
					<Td>{template.isPaid && getCoMarketingBudgetForUser(template, utils.uid)}</Td>
				</Tr>
			);
		});
	}, [slicedCombinedItems, usernames]);

	return (
		<Layout>
			<Flex
				column
				gap="24"
				padding="40px 64px"
			>
				<Flex
					justify="space-between"
					align="center"
				>
					<Typography variant="h1">Needs Attention</Typography>
					<PageSearchFormInput />
				</Flex>

				<Table>
					<THead>
						<Tr>
							<Th>STATUS</Th>
							<Th>RETAILER</Th>
							<Th>TEMPLATE NAME</Th>
							<Th>SCHEDULED</Th>
							<Th>BUDGET</Th>
						</Tr>
					</THead>
					<TBody>{renderedItems}</TBody>
				</Table>
			</Flex>
		</Layout>
	);
}
